import { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAdminAuth } from './contexts/AdminAuthContext';
import { useDataCache } from './contexts/DataCacheContext';
import AdminLayout from './components/layout/AdminLayout';

// Eagerly load auth pages (needed immediately)
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

// Lazy load all other pages for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const ConfigPage = lazy(() => import('./pages/ConfigPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const MonitoringPage = lazy(() => import('./pages/MonitoringPage'));
const OrganizationsPage = lazy(() => import('./pages/OrganizationsPage'));
const KnowledgeBasePage = lazy(() => import('./pages/KnowledgeBasePage'));
const InvitesPage = lazy(() => import('./pages/InvitesPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const StripePage = lazy(() => import('./pages/StripePage'));
const EmailPage = lazy(() => import('./pages/EmailPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const LandingPageMgmtPage = lazy(() => import('./pages/LandingPageMgmtPage'));
const BurningQuestionsPage = lazy(() => import('./pages/BurningQuestionsPage'));

// Loading spinner for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Protected route wrapper
function ProtectedRoute({ children, requiredRole }) {
  const { isAuthenticated, user, loading } = useAdminAuth();
  const { preloadOnLogin } = useDataCache();

  // Store preloadOnLogin in ref to avoid dependency array issues
  const preloadRef = useRef(preloadOnLogin);
  preloadRef.current = preloadOnLogin;

  const hasPreloaded = useRef(false);

  // Preload data once when authenticated
  // preloadOnLogin has its own internal guard, but we also guard here
  useEffect(() => {
    if (isAuthenticated && !loading && !hasPreloaded.current) {
      hasPreloaded.current = true;
      preloadRef.current?.();
    }
  }, [isAuthenticated, loading]); // Removed preloadOnLogin - using ref instead

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role if required
  // Role hierarchy: viewer < pm < operator < admin < super_admin
  if (requiredRole) {
    const roleHierarchy = ['viewer', 'pm', 'operator', 'admin', 'super_admin'];
    const userRoleIndex = roleHierarchy.indexOf(user?.role);
    const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);

    if (userRoleIndex === -1 || requiredRoleIndex === -1 || userRoleIndex < requiredRoleIndex) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}

// Public route wrapper (redirect if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/onboard"
        element={
          <Suspense fallback={<PageLoader />}>
            <OnboardingPage />
          </Suspense>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<PageLoader />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />

      {/* Protected routes - all lazy-loaded pages wrapped in Suspense */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><DashboardPage /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<PageLoader />}><UsersPage /></Suspense>} />
        <Route
          path="employees"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><EmployeesPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="config"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><ConfigPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="audit-logs"
          element={
            <ProtectedRoute requiredRole="super_admin">
              <Suspense fallback={<PageLoader />}><AuditLogsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="monitoring" element={<Suspense fallback={<PageLoader />}><MonitoringPage /></Suspense>} />
        <Route
          path="organizations"
          element={
            <ProtectedRoute requiredRole="operator">
              <Suspense fallback={<PageLoader />}><OrganizationsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="knowledge-base"
          element={
            <ProtectedRoute requiredRole="operator">
              <Suspense fallback={<PageLoader />}><KnowledgeBasePage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="invites"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><InvitesPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="waitlist"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><WaitlistPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="feedback"
          element={
            <ProtectedRoute requiredRole="operator">
              <Suspense fallback={<PageLoader />}><FeedbackPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <ProtectedRoute requiredRole="pm">
              <Suspense fallback={<PageLoader />}><AnalyticsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="support"
          element={
            <ProtectedRoute requiredRole="operator">
              <Suspense fallback={<PageLoader />}><SupportPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="stripe"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><StripePage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="email"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><EmailPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="landing-pages"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><LandingPageMgmtPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="burning-questions"
          element={
            <ProtectedRoute requiredRole="admin">
              <Suspense fallback={<PageLoader />}><BurningQuestionsPage /></Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="jobs"
          element={
            <ProtectedRoute requiredRole="operator">
              <Suspense fallback={<PageLoader />}><JobsPage /></Suspense>
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
